/* Repository path: blue_brief_agentic_workflow/frontend/src/features/estimates/itemBudgetContext.ts */

import type { PlanItem, PlanningBlock, PlanningUnbudgetedRisk } from "../../types";

/**
 * W4-05: the single place the reserve/allowance/risk rules for one item
 * live. `PlanItemCard` and `PlanItemDetail` both call this and render from
 * its result — neither re-derives the rules itself. See
 * docs/architecture/features/W4-05.md §3 "One item-semantics module".
 *
 * A benefit is never included in the monthly reserve — see
 * docs/architecture/features/W4-02.md: benefits are never subtracted from
 * what a person owes. This function assumes that invariant rather than
 * re-deriving it, so a calculated benefit is always classified by its
 * kind, not by `included_in_reserve`.
 */
export type BudgetContext =
  | { kind: "in_reserve" }
  | { kind: "calculated_not_in_reserve" }
  | { kind: "benefit" }
  | { kind: "planning_allowance"; monthlyMin: string; monthlyMax: string }
  | { kind: "one_time_allowance"; annualMin: string; annualMax: string }
  | { kind: "unbudgeted_risk"; reason: PlanningUnbudgetedRisk["reason"] }
  | { kind: "not_in_reserve" }
  | { kind: "none" };

export function budgetContextFor(
  item: PlanItem,
  planning: PlanningBlock | null | undefined,
): BudgetContext {
  const showsReserveExclusion = item.kind === "obligation" || item.kind === "benefit";

  if (item.calculation_state === "calculated") {
    if (item.kind === "benefit") return { kind: "benefit" };
    if (item.kind === "obligation") {
      return item.included_in_reserve
        ? { kind: "in_reserve" }
        : { kind: "calculated_not_in_reserve" };
    }
    return { kind: "none" };
  }

  if (item.budget_role === "planning_allowance") {
    const monthly = planning?.allowances.find((row) => row.item_code === item.code);
    if (monthly) {
      return { kind: "planning_allowance", monthlyMin: monthly.monthly_min, monthlyMax: monthly.monthly_max };
    }
    const oneTime = planning?.one_time_allowances.find((row) => row.item_code === item.code);
    if (oneTime) {
      return { kind: "one_time_allowance", annualMin: oneTime.annual_min, annualMax: oneTime.annual_max };
    }
    // Named a planning allowance by the item itself, but the planning
    // block carries no matching row (a stale or malformed snapshot):
    // degrade to "nothing to say" rather than inventing a range.
    return { kind: "none" };
  }

  if (!showsReserveExclusion) return { kind: "none" };

  const risk = planning?.unbudgeted_risks.find((row) => row.item_code === item.code);
  if (risk && item.budget_role === "guidance_only") {
    return { kind: "unbudgeted_risk", reason: risk.reason };
  }

  return { kind: "not_in_reserve" };
}

/**
 * Plain-English reasons for `unbudgeted_risk`, keyed by the closed server
 * enum. `reason` is typed as a closed union upstream (and
 * `planningBlockIfValid` already rejects a payload carrying an
 * unrecognised value before it reaches here), but this map still falls
 * back to a cautious default for any value it doesn't recognise, rather
 * than assume the type can never lie. See
 * docs/design/features/W4-05.md "Copy/content".
 */
const UNBUDGETED_RISK_REASON_COPY: Record<string, string> = {
  no_reviewed_band:
    "We don’t have a reviewed range for this yet, so we haven’t put a number on it.",
  band_source_lapsed:
    "The official source we use for this range needs re-checking, so we’re not showing a figure.",
  band_profile_gate_failed:
    "Your answers don’t match the situations our reviewed range covers.",
  category_unclassified: "We haven’t classified this one for budgeting yet.",
};

const UNBUDGETED_RISK_REASON_DEFAULT =
  "We haven’t put a number on this one yet. Check officially.";

export function unbudgetedRiskReasonCopy(reason: string): string {
  return UNBUDGETED_RISK_REASON_COPY[reason] ?? UNBUDGETED_RISK_REASON_DEFAULT;
}
